/**
 * UsersManagementPage - Page de gestion des utilisateurs ServUp
 * Inclut les sous-pages Techniciens et Admins
 * Lecture depuis le cache JSON local via /api/users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useHasPermission, ROLE_LABELS } from '../SupabaseWrapper';
import {
  UserIcon,
  UsersIcon,
  ShieldIcon,
  RefreshIcon,
  SearchIcon,
  CheckIcon,
  CloseIcon,
  WrenchIcon,
  UserPlusIcon,
  MailIcon,
} from '../icons';
import TechniciensPage from './TechniciensPage';
import AdminsPage from './AdminsPage';
import { useViewport } from '../hooks/useViewport';
import ResponsiveCardList from '../components/responsive/ResponsiveCardList';

// Type pour les sous-pages
type SubPage = 'users' | 'techniciens' | 'admins';

// Types
interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  role: string;
  user_type: string | null;
  is_active: boolean;
  external_id: string | null;
  company_name: string | null;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
  deleted_at: string | null;
  deleted_by: string | null;
}

// Configuration API
const API_BASE = window.location.origin;

// Couleurs des badges de rôles
const ROLE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  'super_admin': { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
  'account_admin': { bg: '#f3e8ff', text: '#6b21a8', border: '#d8b4fe' },
  'org_admin': { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' },
  'estab_admin': { bg: '#e0e7ff', text: '#3730a3', border: '#6366f1' },
  'technician': { bg: '#f1f5f9', text: '#475569', border: '#94a3b8' }
};

// Labels des types utilisateur
const USER_TYPE_LABELS: Record<string, string> = {
  'T': 'Technicien interne',
  'ST': 'Sous-traitant',
  'A': 'Administrateur'
};

// Props du composant
interface UsersManagementPageProps {
  refreshTrigger?: number;
}

// Formater la date (module scope pour reutilisation par UserMobileCard)
function formatDate(dateStr: string | null) {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
}

// Carte mobile pour UsersManagementPage (rendue par ResponsiveCardList sur < 768px)
function UserMobileCard({
  user,
  actionLoading,
  onDeactivate,
  canDeactivate,
  onResendInvite,
  peutRenvoyer,
}: {
  user: UserProfile;
  actionLoading: string | null;
  onDeactivate: (user: UserProfile) => void;
  canDeactivate: boolean;
  onResendInvite: (user: UserProfile) => void;
  peutRenvoyer: boolean;
}) {
  const roleColor = ROLE_COLORS[user.role] || ROLE_COLORS.technician;
  const isLoading = actionLoading === user.id;
  const isInactive = !user.is_active;
  const fullName = user.first_name || user.last_name
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
    : user.company_name || user.external_id || '-';

  return (
    <div style={{
      backgroundColor: isInactive ? '#fef3c7' : 'white',
      opacity: isInactive ? 0.85 : 1,
      borderRadius: 12,
      padding: 16,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    }}>
      {/* Header avatar + name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 36, height: 36, flexShrink: 0,
          backgroundColor: isInactive ? '#fde68a' : roleColor.bg,
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <UserIcon size={18} color={isInactive ? '#92400e' : roleColor.text} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontWeight: 500,
            color: isInactive ? '#92400e' : '#1e293b',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {fullName}
          </div>
          {user.external_id && (
            <div style={{ fontSize: 12, color: '#94a3b8' }}>
              Technicien : {user.company_name ? `${user.company_name} - ` : ''}{user.external_id}
            </div>
          )}
        </div>
      </div>

      {/* Email */}
      <div style={{ fontSize: 13, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {user.email}
      </div>

      {/* Role + Type + Statut badges */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center',
          padding: '3px 10px',
          backgroundColor: roleColor.bg, color: roleColor.text,
          border: `1px solid ${roleColor.border}`,
          borderRadius: 20, fontSize: 12, fontWeight: 500,
        }}>
          {ROLE_LABELS[user.role] || user.role}
        </span>
        {user.user_type && (
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {USER_TYPE_LABELS[user.user_type] || user.user_type}
          </span>
        )}
        {user.is_active ? (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 10px',
            backgroundColor: '#dcfce7', color: '#166534',
            borderRadius: 20, fontSize: 12, fontWeight: 500,
          }}>
            <CheckIcon size={12} />Actif
          </span>
        ) : (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 10px',
            backgroundColor: '#fef3c7', color: '#92400e',
            borderRadius: 20, fontSize: 12, fontWeight: 500,
          }}>
            <CloseIcon size={12} />Désactivé
          </span>
        )}
      </div>

      {/* Date création */}
      <div style={{ fontSize: 11, color: '#94a3b8' }}>
        Créé le {formatDate(user.created_at)}
      </div>

      {/* Action : Renvoyer l'invitation (rôles web de rang ≤ appelant, comptes actifs — L1bis.7, relecture) */}
      {peutRenvoyer && (
        <div>
          <button
            onClick={() => onResendInvite(user)}
            disabled={isLoading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 12px',
              backgroundColor: '#eff6ff', color: '#1e40af',
              border: '1px solid #bfdbfe', borderRadius: 6,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 500,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            <MailIcon size={14} />
            Renvoyer l’invitation
          </button>
        </div>
      )}

      {/* Action : Désactiver (same condition as desktop) */}
      {user.is_active && canDeactivate && (
        <div>
          <button
            onClick={() => onDeactivate(user)}
            disabled={isLoading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 12px',
              backgroundColor: '#fef3c7', color: '#92400e',
              border: '1px solid #fcd34d', borderRadius: 6,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 500,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            <CloseIcon size={14} />
            Désactiver
          </button>
        </div>
      )}
    </div>
  );
}

function UsersManagementPage({ refreshTrigger = 0 }: UsersManagementPageProps) {
  // RBAC : permissions
  const canView = useHasPermission('users.view');
  const canDeactivate = useHasPermission('users.deactivate');
  // L1bis.7 : inviter un utilisateur WEB (lien « Définir mon mot de passe », 7 jours) et renvoyer l'invitation
  const canInvite = useHasPermission('users.create');
  // À rebrancher quand les fonctionnalités correspondantes arriveront :
  //   useHasPermission('users.update')  → modale d'édition
  //   useHasPermission('users.restore') → restauration après désactivation

  // Sous-page active
  const [subPage, setSubPage] = useState<SubPage>('users');

  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('ALL');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Modale « Inviter un utilisateur » (L1bis.7)
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', first_name: '', last_name: '', role: '', phone: '' });
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [allowedRoles, setAllowedRoles] = useState<Array<{ name: string; display_name: string }>>([]);
  // « Renvoyer l'invitation » seulement là où le backend l'accepte : compte actif, rôle web que
  // l'appelant peut attribuer (allowed-roles = positions ≥ la sienne). Le backend refuse le reste (403/409).
  const peutRenvoyer = (u: UserProfile) =>
    u.is_active && canInvite && u.role !== 'technician' && allowedRoles.some((r) => r.name === u.role);

  // Viewport responsive
  const { isPhone } = useViewport();

  // Récupérer le token SSO
  const getToken = () => localStorage.getItem('sso_token');

  // Charger les utilisateurs depuis l'API
  const fetchUsers = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setError('Token SSO non trouvé');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('[UsersPage] Chargement des utilisateurs...');

      const url = `${API_BASE}/api/users`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('[UsersPage] Response status:', response.status);

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('[UsersPage] Résultat:', result);

      if (result.success) {
        setUsers(result.users || []);
        console.log(`[UsersPage] ${result.count} utilisateur(s) chargé(s)`);
      } else {
        throw new Error(result.error?.message || 'Erreur lors du chargement');
      }

    } catch (err) {
      console.error('[UsersPage] Erreur:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) {
      fetchUsers();
    }
  }, [canView, fetchUsers]);

  // Rôles attribuables chargés dès l'arrivée : ils décident aussi de l'affichage de « Renvoyer l'invitation ».
  useEffect(() => {
    if (canInvite) loadAllowedRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canInvite]);

  // Refetch quand refreshTrigger change (navigation vers cet onglet)
  const isFirstMount = React.useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    if (canView) {
      console.log('[UsersPage] refreshTrigger changed, refetching...');
      fetchUsers();
    }
  }, [refreshTrigger, canView, fetchUsers]);

  // Désactiver un utilisateur (soft delete)
  async function deactivateUser(userId: string, userName: string) {
    if (!confirm(`Désactiver l'utilisateur ${userName} ?\n\nL'utilisateur ne pourra plus se connecter mais son historique sera conservé.\nVous pourrez le réactiver en créant un nouveau compte avec le même email.`)) {
      return;
    }

    const token = getToken();
    if (!token) return;

    setActionLoading(userId);
    setError(null);

    try {
      console.log(`[UsersPage] Désactivation de ${userId}...`);

      const response = await fetch(`${API_BASE}/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      console.log('[UsersPage] Résultat désactivation:', result);

      if (!response.ok || !result.success) {
        throw new Error(result.error?.message || 'Erreur lors de la désactivation');
      }

      setSuccessMessage(`Utilisateur ${userName} désactivé avec succès`);
      setTimeout(() => setSuccessMessage(null), 5000);

      fetchUsers();

    } catch (err) {
      console.error('[UsersPage] Erreur désactivation:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la désactivation');
    } finally {
      setActionLoading(null);
    }
  }

  // Rôles que l'appelant peut attribuer, HORS technician (un technicien naît du pull ERP,
  // page Techniciens). Chargés à l'ouverture de la modale.
  async function loadAllowedRoles() {
    const token = getToken();
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/api/users/allowed-roles`, { headers: { 'Authorization': `Bearer ${token}` } });
      const result = await response.json();
      const roles: Array<{ name: string; display_name: string }> = (result.roles || []).filter((r: { name: string }) => r.name !== 'technician');
      setAllowedRoles(roles);
      // Rôle proposé par défaut : le MOINS puissant permis (liste triée du plus au moins puissant).
      setInviteForm(prev => ({ ...prev, role: prev.role || (roles[roles.length - 1]?.name || '') }));
    } catch (err) {
      console.error('[UsersPage] allowed-roles :', err);
      setAllowedRoles([]);
    }
  }

  function openInviteModal() {
    setInviteError(null);
    setInviteForm({ email: '', first_name: '', last_name: '', role: '', phone: '' });
    setShowInviteModal(true);
    loadAllowedRoles();
  }

  // Inviter un utilisateur web : POST /api/users (aucun mot de passe, aucun external_id)
  async function inviteUser(e: React.FormEvent) {
    e.preventDefault();
    setInviteError(null);
    if (!inviteForm.email.trim()) { setInviteError('L’email est obligatoire.'); return; }
    if (!inviteForm.role) { setInviteError('Le rôle est obligatoire.'); return; }
    const token = getToken();
    if (!token) { setInviteError('Session expirée.'); return; }
    setInviteLoading(true);
    try {
      const body: Record<string, unknown> = {
        email: inviteForm.email.trim().toLowerCase(),
        first_name: inviteForm.first_name.trim(),
        last_name: inviteForm.last_name.trim(),
        role: inviteForm.role,
      };
      if (inviteForm.phone.trim()) body.phone = inviteForm.phone.trim();
      const response = await fetch(`${API_BASE}/api/users`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        setInviteError(result.error?.message || `Erreur ${response.status}`);
        return;
      }
      const notif = result.notifications || {};
      const detail = notif.set_password_link_sent
        ? 'lien « Définir mon mot de passe » envoyé par email, valable 7 jours'
        : (notif.email_error ? `email non envoyé (${notif.email_error}) — utilisez « Renvoyer l’invitation »` : 'aucune notification envoyée — utilisez « Renvoyer l’invitation »');
      setShowInviteModal(false);
      await fetchUsers();
      setSuccessMessage(`${result.action === 'reactivated' ? 'Compte réactivé' : 'Invitation créée'} pour ${body.email} (${detail}).`);
      setTimeout(() => setSuccessMessage(null), 8000);
    } catch (err) {
      console.error('[UsersPage] Erreur invitation :', err);
      setInviteError(err instanceof Error ? err.message : 'Erreur de connexion au serveur.');
    } finally {
      setInviteLoading(false);
    }
  }

  // Renvoyer l'invitation : nouveau lien 7 jours, les anciens ne fonctionnent plus
  async function resendInvite(user: UserProfile) {
    const token = getToken();
    if (!token) return;
    setActionLoading(user.id);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/users/${user.id}/resend-invite`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error?.message || 'Erreur lors du renvoi de l’invitation');
      }
      const notif = result.notifications || {};
      const qui = result.email || user.email || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.company_name || user.id;
      setSuccessMessage(notif.email_sent
        ? `Invitation renvoyée à ${qui} (lien valable 7 jours ; les liens précédents ne fonctionnent plus).`
        : `L’email n’est pas parti pour ${qui}${notif.email_error ? ` (${notif.email_error})` : ''} : aucun nouveau lien, les liens précédents restent valables.`);
      setTimeout(() => setSuccessMessage(null), 8000);
    } catch (err) {
      console.error('[UsersPage] Erreur renvoi invitation :', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du renvoi de l’invitation');
    } finally {
      setActionLoading(null);
    }
  }

  // Filtrer les utilisateurs
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.company_name && user.company_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.external_id && user.external_id.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesRole = filterRole === 'ALL'
      || (filterRole === 'ACTIVE' && user.is_active)
      || (filterRole === 'org_admin' && (user.role === 'org_admin' || user.role === 'super_admin' || user.role === 'estab_admin'))
      || (filterRole === 'technician' && user.role === 'technician');

    return matchesSearch && matchesRole;
  });

  // Stats (uniquement utilisateurs actifs)
  const activeUsers = users.filter(u => !u.deleted_at);
  const stats = {
    total: activeUsers.length,
    admins: activeUsers.filter(u => u.role === 'org_admin' || u.role === 'super_admin' || u.role === 'estab_admin').length,
    technicians: activeUsers.filter(u => u.role === 'technician').length,
    active: activeUsers.filter(u => u.is_active).length
  };

  // Gate d'entree : permission users.view
  if (!canView) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
        gap: '16px'
      }}>
        <ShieldIcon size={64} color="#ef4444" />
        <h2 style={{ color: '#1e293b', margin: 0 }}>Accès refusé</h2>
        <p style={{ color: '#64748b', margin: 0 }}>
          Vous n'avez pas les autorisations nécessaires pour accéder à cette page.
        </p>
      </div>
    );
  }

  // Tabs de navigation
  const tabs = [
    { id: 'users' as SubPage, label: 'Comptes actifs', mobileLabel: 'Comptes', icon: <UsersIcon size={18} /> },
    { id: 'techniciens' as SubPage, label: 'Techniciens', mobileLabel: 'Techs', icon: <WrenchIcon size={18} /> },
    { id: 'admins' as SubPage, label: 'Administrateurs', mobileLabel: 'Admins', icon: <ShieldIcon size={18} /> },
  ];

  // Si sous-page Techniciens ou Admins, afficher directement le composant
  if (subPage === 'techniciens') {
    return (
      <div>
        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: isPhone ? '4px' : '8px',
          marginBottom: isPhone ? '16px' : '24px',
          borderBottom: '1px solid #e2e8f0',
          paddingBottom: isPhone ? '12px' : '16px'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setSubPage(tab.id)}
              style={{
                flex: isPhone ? 1 : undefined,
                display: 'flex',
                alignItems: 'center',
                justifyContent: isPhone ? 'center' : 'flex-start',
                gap: isPhone ? '6px' : '8px',
                padding: isPhone ? '8px 8px' : '10px 20px',
                backgroundColor: subPage === tab.id ? '#3b82f6' : 'white',
                color: subPage === tab.id ? 'white' : '#475569',
                border: `1px solid ${subPage === tab.id ? '#3b82f6' : '#e2e8f0'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: isPhone ? '13px' : '14px',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s'
              }}
            >
              {tab.icon}
              {isPhone ? tab.mobileLabel : tab.label}
            </button>
          ))}
        </div>
        <TechniciensPage />
      </div>
    );
  }

  if (subPage === 'admins') {
    return (
      <div>
        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: isPhone ? '4px' : '8px',
          marginBottom: isPhone ? '16px' : '24px',
          borderBottom: '1px solid #e2e8f0',
          paddingBottom: isPhone ? '12px' : '16px'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setSubPage(tab.id)}
              style={{
                flex: isPhone ? 1 : undefined,
                display: 'flex',
                alignItems: 'center',
                justifyContent: isPhone ? 'center' : 'flex-start',
                gap: isPhone ? '6px' : '8px',
                padding: isPhone ? '8px 8px' : '10px 20px',
                backgroundColor: subPage === tab.id ? '#3b82f6' : 'white',
                color: subPage === tab.id ? 'white' : '#475569',
                border: `1px solid ${subPage === tab.id ? '#3b82f6' : '#e2e8f0'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: isPhone ? '13px' : '14px',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s'
              }}
            >
              {tab.icon}
              {isPhone ? tab.mobileLabel : tab.label}
            </button>
          ))}
        </div>
        <AdminsPage />
      </div>
    );
  }

  // Page principale - Comptes mobiles
  return (
    <div>
      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: isPhone ? '4px' : '8px',
        marginBottom: isPhone ? '16px' : '24px',
        borderBottom: '1px solid #e2e8f0',
        paddingBottom: isPhone ? '12px' : '16px'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setSubPage(tab.id)}
            style={{
              flex: isPhone ? 1 : undefined,
              display: 'flex',
              alignItems: 'center',
              justifyContent: isPhone ? 'center' : 'flex-start',
              gap: isPhone ? '6px' : '8px',
              padding: isPhone ? '8px 8px' : '10px 20px',
              backgroundColor: subPage === tab.id ? '#3b82f6' : 'white',
              color: subPage === tab.id ? 'white' : '#475569',
              border: `1px solid ${subPage === tab.id ? '#3b82f6' : '#e2e8f0'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: isPhone ? '13px' : '14px',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s'
            }}
          >
            {tab.icon}
            {isPhone ? tab.mobileLabel : tab.label}
          </button>
        ))}
      </div>

      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isPhone ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isPhone ? 'stretch' : 'center',
        gap: isPhone ? '12px' : 0,
        marginBottom: '24px'
      }}>
        <div>
          <p style={{ color: '#64748b', margin: 0 }}>
            Comptes cloud créés (techniciens et administrateurs)
          </p>
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexDirection: isPhone ? 'column' : 'row',
          width: isPhone ? '100%' : 'auto'
        }}>
          {canInvite && (
            <button
              onClick={openInviteModal}
              title="Inviter un utilisateur web (lien par email, 7 jours)"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '10px 20px', backgroundColor: '#3b82f6', color: 'white',
                border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
                width: isPhone ? '100%' : 'auto'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
            >
              <UserPlusIcon size={16} />
              Inviter un utilisateur
            </button>
          )}
          <button
            onClick={fetchUsers}
            disabled={loading}
            title="Actualiser les données"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.15s ease',
              width: isPhone ? '100%' : 'auto'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
          >
            <RefreshIcon
              size={16}
              style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }}
            />
            Actualiser
          </button>
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          backgroundColor: '#dcfce7',
          border: '1px solid #86efac',
          borderRadius: '8px',
          marginBottom: '16px',
          color: '#166534'
        }}>
          <CheckIcon size={20} color="#16a34a" />
          {successMessage}
        </div>
      )}

      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          marginBottom: '16px',
          color: '#dc2626'
        }}>
          <CloseIcon size={20} color="#dc2626" />
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <CloseIcon size={16} color="#dc2626" />
          </button>
        </div>
      )}

      {/* Stats — cliquables comme filtres */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isPhone ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: isPhone ? '12px' : '16px',
        marginBottom: '24px'
      }}>
        <StatCard
          icon={<UsersIcon size={24} color="#3b82f6" />}
          label="Total"
          value={stats.total}
          bgColor="#dbeafe"
          accentColor="#3b82f6"
          active={filterRole === 'ALL'}
          onClick={() => setFilterRole('ALL')}
        />
        <StatCard
          icon={<ShieldIcon size={24} color="#f59e0b" />}
          label="Admins"
          value={stats.admins}
          bgColor="#fef3c7"
          accentColor="#f59e0b"
          active={filterRole === 'org_admin'}
          onClick={() => setFilterRole(filterRole === 'org_admin' ? 'ALL' : 'org_admin')}
        />
        <StatCard
          icon={<WrenchIcon size={24} color="#10b981" />}
          label="Techniciens"
          value={stats.technicians}
          bgColor="#dcfce7"
          accentColor="#10b981"
          active={filterRole === 'technician'}
          onClick={() => setFilterRole(filterRole === 'technician' ? 'ALL' : 'technician')}
        />
        <StatCard
          icon={<CheckIcon size={24} color="#8b5cf6" />}
          label="Actifs"
          value={stats.active}
          bgColor="#ede9fe"
          accentColor="#8b5cf6"
          active={filterRole === 'ACTIVE'}
          onClick={() => setFilterRole(filterRole === 'ACTIVE' ? 'ALL' : 'ACTIVE')}
        />
      </div>

      {/* Recherche */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '20px'
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <SearchIcon
            size={18}
            color="#94a3b8"
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)'
            }}
          />
          <input
            type="text"
            placeholder="Rechercher par nom, email ou code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div style={{
        backgroundColor: isPhone ? 'transparent' : 'white',
        borderRadius: '12px',
        border: isPhone ? 'none' : '1px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{
            padding: '48px',
            textAlign: 'center',
            color: '#64748b'
          }}>
            <RefreshIcon
              size={32}
              color="#94a3b8"
              style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }}
            />
            <div>Chargement...</div>
          </div>
        ) : filteredUsers.length === 0 && !isPhone ? (
          <div style={{
            padding: '48px',
            textAlign: 'center',
            color: '#64748b'
          }}>
            <UserIcon size={48} color="#cbd5e1" style={{ marginBottom: '12px' }} />
            <div style={{ marginBottom: '8px' }}>Aucun utilisateur trouvé</div>
            <p style={{ fontSize: '13px', margin: 0 }}>
              Aucun compte créé. Allez dans l'onglet "Techniciens" pour en créer un.
            </p>
          </div>
        ) : (
          <ResponsiveCardList
            items={filteredUsers}
            renderCard={(user) => (
              <UserMobileCard
                user={user}
                actionLoading={actionLoading}
                canDeactivate={canDeactivate}
                peutRenvoyer={peutRenvoyer(user)}
                onResendInvite={resendInvite}
                onDeactivate={(u) => deactivateUser(
                  u.id,
                  u.first_name || u.last_name
                    ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
                    : u.company_name || u.external_id || u.email
                )}
              />
            )}
            emptyMessage="Aucun utilisateur ne correspond aux filtres."
            fallback={
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={thStyle}>Utilisateur</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Rôle</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Date création</th>
                <th style={thStyle}>Statut</th>
                <th style={{ ...thStyle, width: '140px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user, index) => {
                const roleColor = ROLE_COLORS[user.role] || ROLE_COLORS.technician;
                const isLoading = actionLoading === user.id;
                const isInactive = !user.is_active;

                return (
                  <tr
                    key={user.id}
                    style={{
                      backgroundColor: isInactive
                        ? '#fef3c7'
                        : (index % 2 === 0 ? 'white' : '#f8fafc'),
                      opacity: isInactive ? 0.8 : 1
                    }}
                  >
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '36px',
                          height: '36px',
                          backgroundColor: isInactive ? '#fde68a' : roleColor.bg,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <UserIcon size={18} color={isInactive ? '#92400e' : roleColor.text} />
                        </div>
                        <div>
                          <div style={{
                            fontWeight: '500',
                            color: isInactive ? '#92400e' : '#1e293b'
                          }}>
                            {user.first_name || user.last_name
                              ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                              : user.company_name || user.external_id || '-'}
                          </div>
                          {user.external_id && (
                            <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                              Technicien : {user.company_name ? `${user.company_name} - ` : ''}{user.external_id}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ color: '#64748b', fontSize: '14px' }}>
                        {user.email}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '3px 10px',
                        backgroundColor: roleColor.bg,
                        color: roleColor.text,
                        border: `1px solid ${roleColor.border}`,
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {ROLE_LABELS[user.role] || user.role}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: '13px', color: '#64748b' }}>
                        {user.user_type ? USER_TYPE_LABELS[user.user_type] || user.user_type : '-'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                        {formatDate(user.created_at)}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {user.is_active ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 10px',
                          backgroundColor: '#dcfce7',
                          color: '#166534',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          <CheckIcon size={12} />
                          Actif
                        </span>
                      ) : (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 10px',
                          backgroundColor: '#fef3c7',
                          color: '#92400e',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          <CloseIcon size={12} />
                          Désactivé
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {peutRenvoyer(user) && (
                        <button
                          onClick={() => resendInvite(user)}
                          disabled={isLoading}
                          title="Renvoyer l’invitation (nouveau lien valable 7 jours)"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            padding: '6px 12px', marginRight: '8px',
                            backgroundColor: '#eff6ff', color: '#1e40af',
                            border: '1px solid #bfdbfe', borderRadius: '6px',
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            fontSize: '12px', fontWeight: '500', opacity: isLoading ? 0.6 : 1
                          }}
                        >
                          <MailIcon size={14} />
                          Renvoyer l’invitation
                        </button>
                      )}
                      {user.is_active ? (
                        canDeactivate ? (
                          <button
                            onClick={() => deactivateUser(user.id, user.first_name || user.last_name ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : user.company_name || user.external_id || user.email)}
                            disabled={isLoading}
                            title="Désactiver l'utilisateur"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 12px',
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              border: '1px solid #fcd34d',
                              borderRadius: '6px',
                              cursor: isLoading ? 'not-allowed' : 'pointer',
                              fontSize: '12px',
                              fontWeight: '500',
                              opacity: isLoading ? 0.6 : 1
                            }}
                          >
                            <CloseIcon size={14} />
                            Désactiver
                          </button>
                        ) : null
                      ) : (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '6px 12px',
                          backgroundColor: '#f1f5f9',
                          color: '#64748b',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          Recréer via l'onglet "Techniciens"
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
            }
          />
        )}
      </div>

      {/* Info footer */}
      {users.length > 0 && (
        <div style={{
          marginTop: '16px',
          padding: '16px',
          backgroundColor: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#0369a1',
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start'
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0369a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <ul style={{ margin: 0, paddingLeft: '16px' }}>
            <li><strong>Désactiver un utilisateur</strong> : L'utilisateur ne peut plus se connecter mais son historique est conservé</li>
            <li><strong>Réactiver un utilisateur</strong> : Allez dans l'onglet "Techniciens", sélectionnez le technicien et créez un compte avec le même email. Le compte sera automatiquement réactivé avec un nouveau mot de passe.</li>
            <li><strong>Créer un compte mobile</strong> : Pour un nouveau technicien, utilisez également l'onglet "Techniciens"</li>
            <li><strong>Inviter un utilisateur web</strong> : bouton « Inviter un utilisateur » — il reçoit par email un lien « Définir mon mot de passe » valable 7 jours ; « Renvoyer l’invitation » en crée un nouveau (les précédents ne fonctionnent plus)</li>
          </ul>
        </div>
      )}

      {/* Modale « Inviter un utilisateur » (L1bis.7, Q16-c) */}
      {showInviteModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}
          onClick={() => { if (!inviteLoading) setShowInviteModal(false); }}>
          <div style={{ width: '100%', maxWidth: '480px', backgroundColor: 'white', borderRadius: '12px', padding: isPhone ? '20px' : '28px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}
            onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Inviter un utilisateur</h2>
            <p style={{ margin: '0 0 18px', fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
              Les techniciens se créent depuis la page « Techniciens » (ils naissent de l’ERP). Les autres rôles reçoivent par email un lien « Définir mon mot de passe » valable 7 jours, à usage unique.
            </p>
            <form onSubmit={inviteUser}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Email *</label>
                <input type="email" required autoFocus value={inviteForm.email} onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexDirection: isPhone ? 'column' : 'row' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Prénom</label>
                  <input type="text" value={inviteForm.first_name} onChange={(e) => setInviteForm(prev => ({ ...prev, first_name: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Nom</label>
                  <input type="text" value={inviteForm.last_name} onChange={(e) => setInviteForm(prev => ({ ...prev, last_name: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Rôle *</label>
                <select required value={inviteForm.role} onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', backgroundColor: 'white' }}>
                  {allowedRoles.length === 0 && <option value="">Chargement des rôles…</option>}
                  {allowedRoles.map((r) => <option key={r.name} value={r.name}>{r.display_name || ROLE_LABELS[r.name] || r.name}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Téléphone portable (facultatif)</label>
                <input type="tel" value={inviteForm.phone} placeholder="+590690123456" onChange={(e) => setInviteForm(prev => ({ ...prev, phone: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#6b7280', lineHeight: 1.5 }}>
                  Sert à « Mot de passe oublié » (code envoyé par SMS). <strong>Sans téléphone, « Mot de passe oublié » ne fonctionne pas</strong> : la réinitialisation passe par « Renvoyer l’invitation ».
                </p>
              </div>
              {inviteError && (
                <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '13px', color: '#991b1b' }}>{inviteError}</div>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="button" onClick={() => setShowInviteModal(false)} disabled={inviteLoading}
                  style={{ flex: 1, padding: '10px', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
                  Annuler
                </button>
                <button type="submit" disabled={inviteLoading || allowedRoles.length === 0}
                  style={{ flex: 1, padding: '10px', backgroundColor: inviteLoading ? '#93c5fd' : '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: inviteLoading ? 'not-allowed' : 'pointer' }}>
                  {inviteLoading ? 'Envoi…' : 'Envoyer l’invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Composants auxiliaires
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  bgColor: string;
  accentColor?: string;
  active?: boolean;
  onClick?: () => void;
}

function StatCard({ icon, label, value, bgColor, accentColor, active, onClick }: StatCardProps) {
  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        border: active ? `2px solid ${accentColor || '#3b82f6'}` : '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        boxShadow: active ? `0 0 0 1px ${accentColor || '#3b82f6'}20` : 'none'
      }}
    >
      <div style={{
        width: '48px',
        height: '48px',
        backgroundColor: bgColor,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: '28px', fontWeight: '600', color: '#1e293b' }}>
          {value}
        </div>
        <div style={{ fontSize: '14px', color: active ? (accentColor || '#3b82f6') : '#64748b', fontWeight: active ? '600' : '400' }}>
          {label}
        </div>
      </div>
    </div>
  );
}

// Styles pour la table
const thStyle: React.CSSProperties = {
  padding: '14px 16px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: '600',
  color: '#475569',
  borderBottom: '1px solid #e2e8f0'
};

const tdStyle: React.CSSProperties = {
  padding: '14px 16px',
  borderBottom: '1px solid #e2e8f0',
  fontSize: '14px'
};

export default UsersManagementPage;
